import React from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Link,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import IconMi from 'components/common/icon/IconMi';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import SourceTypeIcon from 'components/common/source-type-icon/SourceTypeIcon';
import { IKgModelCard, KgCardAction } from './types';

type Translate = (key: string, params?: Record<string, unknown>) => string;

interface Props {
    card: IKgModelCard;
    pending: boolean;
    t: Translate;
    onAction: (card: IKgModelCard, action: KgCardAction) => void;
    onOpenTestSession: (card: IKgModelCard) => void;
}

const SUMMARY_KEYS: Record<IKgModelCard['state'], { first: string; incremental: string }> = {
    not_in_graph: { first: 'summary_not_in_graph', incremental: 'summary_not_in_graph' },
    in_graph_not_added: { first: 'summary_in_graph_not_added', incremental: 'summary_in_graph_not_added' },
    profiling: { first: 'summary_profiling_first', incremental: 'summary_profiling_incremental' },
    review_required: { first: 'summary_review_first', incremental: 'summary_review_incremental' },
    available: { first: 'summary_available_first', incremental: 'summary_available_incremental' },
};

export default function ModelCard({ card, pending, t, onAction, onOpenTestSession }: Props) {
    const tParams = { model: card.name, noun: card.model_term };

    const pill = (() => {
        switch (card.state) {
            case 'profiling':
                return {
                    label: t(card.incremental ? 'pill_profiling_incremental' : 'pill_profiling_first'),
                    color: 'primary' as const,
                    icon: <CircularProgress size={12} color="inherit" />,
                };
            case 'review_required':
                return { label: t('pill_review_required'), color: 'warning' as const, icon: <IconMi icon="alert" /> };
            case 'available':
                return { label: t('pill_available'), color: 'success' as const, icon: <IconMi icon="check" /> };
            case 'in_graph_not_added':
                return {
                    label: t('pill_in_graph_not_added'),
                    color: 'default' as const,
                    icon: <IconMi icon="catalog" />,
                };
            default:
                return { label: t('pill_not_in_graph'), color: 'default' as const, icon: <IconMi icon="catalog" /> };
        }
    })();

    const typeTag = card.bi_tool === 'other' ? t('tag_other', tParams) : t(`tag_${card.bi_tool}`);
    const testLabel = card.bi_tool === 'tableau' ? t('btn_test_tableau') : t('btn_test');

    const viewKgButton = (primary: boolean) => (
        <Tooltip title={card.knowledge_graph_url ? undefined : t('view_in_kg_no_node', tParams)}>
            <Box sx={{ display: 'inline-flex' }}>
                <Button
                    variant={primary ? 'contained' : 'text'}
                    component="a"
                    href={card.knowledge_graph_url ?? '#'}
                    target="_blank"
                    disabled={!card.knowledge_graph_url}
                    endIcon={<IconMi icon="external-link" />}
                    data-test="concierge_kg_link_view"
                >
                    {t('btn_view_kg')}
                </Button>
            </Box>
        </Tooltip>
    );

    const addButton = (
        <Button
            variant="contained"
            startIcon={<IconMi icon="new" />}
            disabled={pending}
            onClick={() => onAction(card, 'add')}
            data-test="concierge_kg_btn_add"
        >
            {t('btn_add')}
        </Button>
    );

    const testButton = (
        <Button variant="text" onClick={() => onOpenTestSession(card)} data-test="concierge_kg_btn_test">
            {testLabel}
        </Button>
    );

    const openItemsLine = card.state === 'review_required' && (
        <Typography variant="body2" color="text.secondary" data-test="concierge_kg_open_items">
            {card.open_item_count > 0 ? (
                <>
                    <Link
                        href={card.knowledge_graph_url ?? '#'}
                        target="_blank"
                        sx={{ fontWeight: 600 }}
                        data-test="concierge_kg_open_items_link"
                    >
                        {t('open_items_count', { count: card.open_item_count })}
                    </Link>{' '}
                    {t('open_items_rest', tParams)}
                </>
            ) : (
                t('open_items_zero', tParams)
            )}
        </Typography>
    );

    const usage = card.usage;
    const usageList = usage.total > 0 && (
        <Box data-test="concierge_kg_usage">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('usage_title', tParams)}
            </Typography>
            <Stack spacing={0.25}>
                {usage.reports.map((report) => (
                    <Stack key={report.element_id} direction="row" spacing={1} alignItems="baseline">
                        <Link href={`/extreport/${report.element_id}`} target="_blank" variant="body2">
                            {report.name}
                        </Link>
                        {report.processed_time ? (
                            <Typography variant="body2" color="text.secondary">
                                {t('usage_processed', { date: dayjs(report.processed_time).format('MMM D, YYYY') })}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {t('usage_not_processed')}
                            </Typography>
                        )}
                    </Stack>
                ))}
                {usage.total > usage.reports.length && (
                    <Typography variant="body2" color="text.secondary">
                        {t('usage_more', { count: usage.total - usage.reports.length })}
                    </Typography>
                )}
            </Stack>
        </Box>
    );

    return (
        <Box
            sx={{
                position: 'relative',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
            }}
            data-test={`concierge_kg_card_${card.identity_id}`}
        >
            <LoadingPlaceholder open={pending} />

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                {card.bi_tool !== 'other' && (
                    <Tooltip title={typeTag}>
                        <Box sx={{ display: 'inline-flex' }}>
                            <SourceTypeIcon sourceType={card.bi_tool} />
                        </Box>
                    </Tooltip>
                )}
                <Typography sx={{ fontWeight: 600 }}>{card.name}</Typography>
                <Chip label={typeTag} size="small" variant="outlined" />
                <Box sx={{ flexGrow: 1 }} />
                <Chip
                    label={pill.label}
                    color={pill.color}
                    icon={pill.icon as React.ReactElement}
                    data-test="concierge_kg_pill"
                />
            </Stack>

            <Typography variant="body2" color="text.secondary" data-test="concierge_kg_summary">
                {t(SUMMARY_KEYS[card.state][card.incremental ? 'incremental' : 'first'], tParams)}
            </Typography>

            {openItemsLine}

            {card.state === 'profiling' && <LinearProgress data-test="concierge_kg_progress_bar" />}

            {usageList}

            {card.state !== 'profiling' && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {card.state === 'not_in_graph' && addButton}
                    {card.state === 'in_graph_not_added' && (
                        <>
                            {addButton}
                            {card.knowledge_graph_url && viewKgButton(false)}
                        </>
                    )}
                    {card.state === 'review_required' && (
                        <>
                            {viewKgButton(true)}
                            {testButton}
                        </>
                    )}
                    {card.state === 'available' && (
                        <>
                            {viewKgButton(true)}
                            {testButton}
                            <Tooltip title={t('btn_remove')}>
                                <Box sx={{ display: 'inline-flex', ml: 'auto' }}>
                                    <IconButton
                                        color="error"
                                        size="small"
                                        disabled={pending}
                                        onClick={() => onAction(card, 'remove')}
                                        data-test="concierge_kg_remove"
                                        sx={{ color: 'error.main' }}
                                    >
                                        <IconMi icon="trash" />
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        </>
                    )}
                </Stack>
            )}
        </Box>
    );
}
